import React, {
  createContext,
  useEffect,
  useState,
  useCallback,
  useContext,
} from "react";
import { AuthContext } from "../Context/AuthContext";
import { toast } from "react-toastify";
import { callMultipleApis } from "../services/ApiServices";

export const ShopContext = createContext(null);
const getProductList = process.env.REACT_APP_GET_PRODUCT_LIST_URL;
const setWishlist = process.env.REACT_APP_SET_WISHLIST_URL;

const ShopContextProvider = ({ children }) => {
  const auth = useContext(AuthContext);
  const authLoading = auth?.authLoading;
  const userDetail = auth?.userDetails;

  const [all_products, setAllProducts] = useState([]);
  const [popular_products, setPopularProducts] = useState([]);
  const [newCollection, setNewCollection] = useState([]);
  const [cartItems, setCartItems] = useState({});
  const [loading, setLoading] = useState(true);

  // 🔹 Fetch products (NO BLOCKING)
  const fetchProductList = useCallback(async () => {
    try {
      setLoading(true);

      const emailid = userDetail?.emailid || "";

      const [allRes, popularRes, newCollectionRes] = await callMultipleApis([
        {
          url: getProductList,
          method: "POST",
          data: { action_type: "ALL", email: emailid, },
          params: {},
          headers: {},
        },
        {
          url: getProductList,
          method: "POST",
          data: { action_type: "POPULARPRODUCTS", email: emailid, },
          params: {},
          headers: {},
        },
        {
          url: getProductList,
          method: "POST",
          data: { action_type: "NEWCOLLECTIONS", email: emailid, },
          params: {},
          headers: {},
        },
      ]);
      setAllProducts(allRes?.result || []);
      setPopularProducts(popularRes?.result || []);
      setNewCollection(newCollectionRes?.result || []);
    } catch (error) {
      console.error("❌ Product fetch failed:", error);
    } finally {
      setLoading(false);
    }
  }, [userDetail]); // 🔑 IMPORTANT

  // 🔹 Re-fetch when auth finishes OR user changes
  useEffect(() => {
    if (!authLoading) {
      fetchProductList();
    }
  }, [authLoading, fetchProductList]);

  // 🔹 Local wishlist toggle
  const toggleWishlistLocal = (productId, nextWishlist) => {
    setAllProducts((prev) =>
      prev.map((p) =>
        p.item_id === productId
          ? { ...p, is_wishlist: nextWishlist }
          : p
      )
    );

    setPopularProducts((prev) =>
      prev.map((p) =>
        p.item_id === productId
          ? { ...p, is_wishlist: nextWishlist }
          : p
      )
    );

    setNewCollection((prev) =>
      prev.map((p) =>
        p.item_id === productId
          ? { ...p, is_wishlist: nextWishlist }
          : p
      )
    );
  };

  // 🔹 Wishlist API
  const toggleWishlist = async (productId) => {
    if (authLoading) return;

    if (!userDetail) {
      toast.info("Please login to use wishlist ❤️");
      return;
    }

    const product = all_products.find((p) => p.item_id === productId);
    if (!product) return;

    // 🔥 calculate next value BEFORE UI update
    const nextWishlist = product.is_wishlist ? 0 : 1;

    toggleWishlistLocal(productId, nextWishlist); // optimistic UI

    try {
      await callMultipleApis([
        {
          url: setWishlist,
          method: "POST",
          data: { itemid: productId, iswishlist: product.is_wishlist, email: userDetail.emailid, },
          params: {},
          headers: {},
        }
      ]);

    } catch (err) {
      toggleWishlistLocal(productId, product.is_wishlist); // rollback
      console.error(err);
    }
  };

  // 🛒 Cart
  const addToCart = (itemId) => {
    setCartItems((prev) => ({
      ...prev,
      [itemId]: (prev[itemId] || 0) + 1,
    }));
  };

  const removeToCart = (itemId) => {
    setCartItems((prev) => {
      const updated = { ...prev };
      if (!updated[itemId]) return prev;
      updated[itemId] -= 1;
      if (updated[itemId] <= 0) delete updated[itemId];
      return updated;
    });
  };

  const getTotalCartAmount = () =>
    Object.entries(cartItems).reduce((total, [itemId, qty]) => {
      const product = all_products.find(
        (p) => Number(p.item_id) === Number(itemId)
      );
      return product ? total + qty * Number(product.new_price) : total;
    }, 0);

  const getTotalCartItems = () =>
    Object.values(cartItems).reduce((sum, qty) => sum + qty, 0);

  return (
    <ShopContext.Provider
      value={{
        loading,
        all_products,
        popular_products,
        newCollection,
        cartItems,
        addToCart,
        removeToCart,
        getTotalCartAmount,
        getTotalCartItems,
        toggleWishlist,
      }}
    >
      {children}
    </ShopContext.Provider>
  );
};

export default ShopContextProvider;
